/**
 * TasksStep — create-wizard step 2 (W12 Part B, Task B3).
 *
 * The task routes the pinned insight analysis spans; each selected task becomes
 * one route of the generated funnel. Props-in / JSX-out — the defaults, the
 * path writes and the readiness consequences live in `StrategyFormDialog`.
 */
import React from 'react';
import { Typography } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { SelectionTable, type SelectionRow } from '../SelectionTable';

export interface TasksStepProps {
  rows: SelectionRow[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  loading?: boolean;
  error?: string | null;
  /** No analysis pinned yet — step 1 is the only required decision. */
  pinned: boolean;
}

export const TasksStep: React.FC<TasksStepProps> = ({
  rows,
  selectedIds,
  onSelectedIdsChange,
  loading = false,
  error = null,
  pinned,
}) => (
  <SelectionTable
    rows={rows}
    selectedIds={selectedIds}
    onSelectedIdsChange={onSelectedIdsChange}
    loading={loading}
    error={error}
    searchPlaceholder="Search tasks"
    summary={`${selectedIds.length} of ${rows.length} selected · one funnel route per selected task`}
    emptyState={(
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        {pinned
          ? 'This insight analysis spans no task routes yet — add tasks to the project first.'
          : 'Pin an insight analysis on the first step: its tasks become the routes offered here.'}
      </Typography>
    )}
    aria-label="Task routes"
    data-testid={STRATEGY_LIST.wizardTable('tasks')}
  />
);

TasksStep.displayName = 'TasksStep';
